// Linha do tempo das últimas 24 h.
//
// O monitor responde "como ela está agora". Esta tela responde a pergunta que
// vem depois, e que ninguém consegue responder hoje: "como foi a noite?".
import { useEffect, useMemo, useState } from 'react';
import {
  formatarHora,
} from '@medicov1/shared';
import type { AnotacaoDTO, LeituraDTO, TipoLeitura } from '@medicov1/shared';
import { chamar } from '../api';
import { ROTULO_ORIGEM } from './estadoDoDado';
import { NotaFormatada } from './NotaFormatada';
import './relatorio.css';
import './linhaDoTempo.css';

const SERIES: { tipo: TipoLeitura; rotulo: string; cor: string; min: number; max: number }[] = [
  { tipo: 'glicemia', rotulo: 'Glicemia', cor: 'var(--glicemia)', min: 40, max: 300 },
  { tipo: 'frequencia_cardiaca', rotulo: 'FC', cor: 'var(--fc)', min: 40, max: 140 },
  { tipo: 'pa_sistolica', rotulo: 'PA sistólica', cor: 'var(--pa)', min: 70, max: 180 },
  { tipo: 'saturacao', rotulo: 'SpO₂', cor: 'var(--spo2)', min: 80, max: 100 },
  { tipo: 'temperatura', rotulo: 'Temperatura', cor: 'var(--temperatura)', min: 34, max: 41 },
];

const ROTULO_TIPO: Partial<Record<TipoLeitura, string>> = {
  glicemia: 'Glicemia', frequencia_cardiaca: 'FC', pa_sistolica: 'PA sistólica',
  pa_diastolica: 'PA diastólica', saturacao: 'SpO₂', temperatura: 'Temperatura',
  frequencia_respiratoria: 'Freq. respiratória', diurese: 'Diurese',
  evacuacoes: 'Evacuações', dieta_enteral: 'Dieta enteral',
  balanco_hidrico: 'Balanço hídrico', peso: 'Peso',
};

const COR_TIPO: Partial<Record<TipoLeitura, string>> = {
  glicemia: 'var(--glicemia)', frequencia_cardiaca: 'var(--fc)',
  pa_sistolica: 'var(--pa)', pa_diastolica: 'var(--pa)',
  saturacao: 'var(--spo2)', temperatura: 'var(--temperatura)',
};

const hora = (iso: string) =>
  formatarHora(iso);

export function LinhaDoTempo({ pacienteId, aoVoltar }: { pacienteId: string; aoVoltar: () => void }) {
  const [leituras, setLeituras] = useState<LeituraDTO[]>([]);
  const [anotacoes, setAnotacoes] = useState<AnotacaoDTO[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    const desde = new Date(Date.now() - 24 * 3600_000).toISOString();
    Promise.all([
      chamar<LeituraDTO[]>(`/leituras?pacienteId=${pacienteId}&desde=${desde}&limite=500`),
      chamar<AnotacaoDTO[]>(`/anotacoes?pacienteId=${pacienteId}&limite=100`),
    ])
      .then(([ls, as]) => {
        setLeituras(ls);
        // A anotação é o registro mais humano que existe aqui: o número diz o
        // que aconteceu, ela diz como foi.
        setAnotacoes(as.filter((a) => new Date(a.ocorreuEm).getTime() >= Date.now() - 24 * 3600_000));
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'não consegui carregar'))
      .finally(() => setCarregando(false));
  }, [pacienteId]);

  const { inicio, fim } = useMemo(() => {
    const agora = Date.now();
    return { inicio: agora - 24 * 3600_000, fim: agora };
  }, [leituras]);

  // Pressão grava três leituras do mesmo evento; na lista elas viram uma linha.
  // Anotações entram na mesma ordem cronológica das medidas.
  type Evento =
    | { chave: string; quando: number; tipo: 'medida'; itens: LeituraDTO[] }
    | { chave: string; quando: number; tipo: 'anotacao'; anotacao: AnotacaoDTO };

  const eventos = useMemo<Evento[]>(() => {
    const porGrupo = new Map<string, LeituraDTO[]>();
    const soltas: LeituraDTO[] = [];
    for (const l of leituras) {
      if (l.grupo) porGrupo.set(l.grupo, [...(porGrupo.get(l.grupo) ?? []), l]);
      else soltas.push(l);
    }

    const medidas: Evento[] = [
      ...[...porGrupo.values()].map((g) => ({
        chave: g[0]!.grupo!, quando: new Date(g[0]!.medidoEm).getTime(),
        tipo: 'medida' as const, itens: g,
      })),
      ...soltas.map((l) => ({
        chave: l.id, quando: new Date(l.medidoEm).getTime(),
        tipo: 'medida' as const, itens: [l],
      })),
    ];

    const escritas: Evento[] = anotacoes.map((a) => ({
      chave: a.id, quando: new Date(a.ocorreuEm).getTime(),
      tipo: 'anotacao' as const, anotacao: a,
    }));

    return [...medidas, ...escritas].sort((a, b) => b.quando - a.quando);
  }, [leituras, anotacoes]);

  return (
    <main className="tempo">
      <header className="tempo__topo">
        <div>
          <h1>Últimas 24 horas</h1>
          <p className="tempo__resumo">
            {carregando
              ? 'carregando…'
              : `${leituras.length} medidas · ${anotacoes.length} ${anotacoes.length === 1 ? 'anotação' : 'anotações'}`}
          </p>
        </div>
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
      </header>

      {erro && <p className="aviso-erro">{erro}</p>}

      <section className="tempo__graficos">
        {SERIES.map((serie) => {
          const pontos = leituras
            .filter((l) => l.tipo === serie.tipo)
            .map((l) => ({ x: new Date(l.medidoEm).getTime(), y: Number(l.valor), origem: l.origem }))
            .sort((a, b) => a.x - b.x);

          return (
            <article className="serie" key={serie.tipo} style={{ ['--cor' as string]: serie.cor }}>
              <div className="serie__cabeca">
                <b>{serie.rotulo}</b>
                <span>
                  {pontos.length === 0
                    ? 'sem registro nas últimas 24 h'
                    : `${pontos.length} ${pontos.length === 1 ? 'medida' : 'medidas'} · último ${pontos[pontos.length - 1]!.y.toLocaleString('pt-BR')}`}
                </span>
              </div>
              <Grafico pontos={pontos} inicio={inicio} fim={fim} min={serie.min} max={serie.max} />
            </article>
          );
        })}
      </section>

      <h2 className="tempo__titulo">Registros</h2>
      <ol className="tempo__lista">
        {eventos.length === 0 && !carregando && (
          <li className="tempo__vazio">Nenhum registro nas últimas 24 horas.</li>
        )}
        {eventos.map((evento) => {
          if (evento.tipo === 'anotacao') {
            const { anotacao } = evento;
            // Cada anotação é uma folha à parte, no padrão do relatório: na
            // passada de olho, onde uma termina e a outra começa fica claro.
            return (
              <li className="tempo__nota" key={evento.chave}>
                <article className="doc folha-nota">
                  <header className="folha-nota__topo">
                    <b>{hora(anotacao.ocorreuEm)}</b>
                    <span>anotação · {anotacao.autor.nome}</span>
                  </header>
                  <NotaFormatada texto={anotacao.texto} />
                </article>
              </li>
            );
          }

          const primeiro = evento.itens[0]!;
          return (
            <li className="evento" key={evento.chave} style={{ ['--cor' as string]: COR_TIPO[primeiro.tipo] ?? 'var(--texto-3)' }}>
              <span className="evento__hora">{hora(primeiro.medidoEm)}</span>
              <span className="evento__marca" />
              <span className="evento__texto">
                <b>
                  {evento.itens
                    .map((l) => `${ROTULO_TIPO[l.tipo] ?? l.tipo} ${Number(l.valor).toLocaleString('pt-BR')} ${l.unidade}`)
                    .join(' · ')}
                </b>
                <small>
                  {ROTULO_ORIGEM[primeiro.origem] ?? primeiro.origem}
                  {primeiro.aparelho ? ` · ${primeiro.aparelho.apelido}` : ''}
                  {primeiro.autor ? ` · ${primeiro.autor.nome}` : ''}
                </small>
              </span>
            </li>
          );
        })}
      </ol>
    </main>
  );
}

/** Gráfico simples em SVG: nada de biblioteca para desenhar cinco linhas. */
function Grafico({
  pontos, inicio, fim, min, max,
}: {
  pontos: { x: number; y: number; origem: string }[];
  inicio: number; fim: number; min: number; max: number;
}) {
  const L = 34, R = 8, T = 10, B = 18, W = 640, H = 96;

  if (pontos.length === 0) {
    return (
      <svg className="grafico" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <line x1={L} y1={H / 2} x2={W - R} y2={H / 2} className="grafico__vazio" />
      </svg>
    );
  }

  // A escala acompanha o dado quando ele sai da faixa esperada, senão o ponto
  // some do gráfico justamente quando é o mais importante de ver.
  const valores = pontos.map((p) => p.y);
  const baixo = Math.min(min, ...valores);
  const alto = Math.max(max, ...valores);

  const px = (x: number) => L + ((x - inicio) / (fim - inicio)) * (W - L - R);
  const py = (y: number) => T + (1 - (y - baixo) / (alto - baixo)) * (H - T - B);

  const caminho = pontos.map((p, i) => `${i ? 'L' : 'M'}${px(p.x).toFixed(1)} ${py(p.y).toFixed(1)}`).join(' ');

  return (
    <svg className="grafico" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <line x1={L} y1={T} x2={W - R} y2={T} className="grafico__grade" />
      <line x1={L} y1={H - B} x2={W - R} y2={H - B} className="grafico__grade" />
      <text x={2} y={T + 5} className="grafico__rotulo">{Math.round(alto)}</text>
      <text x={2} y={H - B + 4} className="grafico__rotulo">{Math.round(baixo)}</text>
      <text x={L} y={H - 4} className="grafico__rotulo">24 h atrás</text>
      <text x={W - R} y={H - 4} className="grafico__rotulo" textAnchor="end">agora</text>

      {pontos.length > 1 && <path d={caminho} className="grafico__linha" />}
      {pontos.map((p, i) => (
        <circle
          key={i}
          cx={px(p.x)} cy={py(p.y)}
          r={i === pontos.length - 1 ? 4 : 2.8}
          className={p.origem === 'manual' ? 'grafico__ponto grafico__ponto--manual' : 'grafico__ponto'}
        />
      ))}
    </svg>
  );
}
